/**
 * Simon Says Game
 * Repeat the color sequence that gets longer with each round
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const SimonSays = ({ onHome }) => {
  // Color configuration
  const COLOR_CONFIG = [
    { id: 0, name: 'Red', hex: '#ff6b6b', rgbGlow: '255, 107, 107' },
    { id: 1, name: 'Blue', hex: '#4ecdc4', rgbGlow: '78, 205, 196' },
    { id: 2, name: 'Green', hex: '#95e77d', rgbGlow: '149, 231, 125' },
    { id: 3, name: 'Yellow', hex: '#ffe66d', rgbGlow: '255, 230, 109' },
  ];

  // Game State
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [sequence, setSequence] = useState([]);
  const [userSequence, setUserSequence] = useState([]);
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  const [activePad, setActivePad] = useState(null);
  const [score, setScore] = useLocalStorage('simonSays_score', 0);
  const [highScore, setHighScore] = useLocalStorage('simonSays_highScore', 0);
  const [difficulty, setDifficulty] = useState('normal'); // easy, normal, hard
  const audioRef = useRef({});

  // Initialize audio context (using Web Audio API for tone generation)
  useEffect(() => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Create tone generator function
    const playTone = (frequency, duration) => {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      } catch (e) {
        // Audio context might not be available
      }
    };

    audioRef.current = {
      red: () => playTone(261.63, 0.5),    // C4
      blue: () => playTone(329.63, 0.5),   // E4
      green: () => playTone(392.0, 0.5),   // G4
      yellow: () => playTone(523.25, 0.5), // C5
    };
  }, []);

  const getDifficultySpeed = () => {
    const speeds = {
      easy: 1000,
      normal: 600,
      hard: 300,
    };
    return speeds[difficulty] || 600;
  };

  const startGame = (selectedDifficulty = 'normal') => {
    setDifficulty(selectedDifficulty);
    setSequence([Math.floor(Math.random() * 4)]);
    setUserSequence([]);
    setGameState(GAME_CONFIG.states.PLAYING);
    setScore(0);
  };

  const playSequence = async () => {
    setIsPlayingSequence(true);
    const speed = getDifficultySpeed();

    for (let i = 0; i < sequence.length; i++) {
      await new Promise((resolve) => {
        setTimeout(() => {
          flashPad(sequence[i]);
          resolve();
        }, speed);
      });
    }

    setIsPlayingSequence(false);
  };

  useEffect(() => {
    if (gameState === GAME_CONFIG.states.PLAYING && sequence.length > 0 && userSequence.length === 0) {
      const timer = setTimeout(() => playSequence(), 500);
      return () => clearTimeout(timer);
    }
  }, [gameState, sequence, userSequence]);

  const flashPad = (colorId) => {
    setActivePad(colorId);

    // Play tone
    const color = COLOR_CONFIG[colorId];
    const audioKey = color.name.toLowerCase();
    if (audioRef.current[audioKey]) {
      audioRef.current[audioKey]();
    }

    setTimeout(() => setActivePad(null), 200);
  };

  const handlePadClick = async (colorId) => {
    if (gameState !== GAME_CONFIG.states.PLAYING || isPlayingSequence) return;

    flashPad(colorId);

    const newUserSequence = [...userSequence, colorId];
    setUserSequence(newUserSequence);

    // Check if user's input matches sequence
    if (newUserSequence[newUserSequence.length - 1] !== sequence[newUserSequence.length - 1]) {
      // Wrong! Game Over
      setGameState(GAME_CONFIG.states.GAME_OVER);
      if (score > highScore) {
        setHighScore(score);
      }
      return;
    }

    // User completed the sequence correctly
    if (newUserSequence.length === sequence.length) {
      // Advance to next round
      const newScore = score + (sequence.length * 10);
      setScore(newScore);

      if (sequence.length >= 30) {
        // Win condition: reached max sequence length
        setGameState(GAME_CONFIG.states.WIN);
        if (newScore > highScore) {
          setHighScore(newScore);
        }
      } else {
        // Add new color to sequence and prepare for next round
        setUserSequence([]);
        const nextSequence = [...sequence, Math.floor(Math.random() * 4)];
        setSequence(nextSequence);
      }
    }
  };

  const handleRestart = () => {
    setSequence([]);
    setUserSequence([]);
    setGameState(GAME_CONFIG.states.MENU);
    setActivePad(null);
  };

  const levelText = Math.max(1, sequence.length);

  return (
    <GameLayout
      title="Simon Says 🎯"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={handleRestart}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Choose Difficulty</h2>
            <p style={styles.menuDescription}>
              Repeat the color sequences that get longer with each round. Choose your difficulty:
            </p>

            <div style={styles.difficultyGrid}>
              <button
                onClick={() => startGame('easy')}
                style={styles.difficultyButton}
                className="btn btn-success"
              >
                🟢 Easy<br />
                <span style={styles.difficultyDesc}>Slower sequences</span>
              </button>
              <button
                onClick={() => startGame('normal')}
                style={styles.difficultyButton}
                className="btn btn-primary"
              >
                🟡 Normal<br />
                <span style={styles.difficultyDesc}>Balanced speed</span>
              </button>
              <button
                onClick={() => startGame('hard')}
                style={styles.difficultyButton}
                className="btn btn-danger"
              >
                🔴 Hard<br />
                <span style={styles.difficultyDesc}>Fast sequences</span>
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            {/* Level Display */}
            <div style={styles.levelDisplay}>
              <div style={styles.levelLabel}>Level</div>
              <div style={styles.levelValue}>{levelText}</div>
            </div>

            {/* Game Status */}
            <div style={styles.statusDisplay}>
              {isPlayingSequence ? (
                <span style={{ color: GAME_CONFIG.colors.accent2 }}>Watch the sequence...</span>
              ) : userSequence.length === 0 ? (
                <span style={{ color: GAME_CONFIG.colors.accent }}>Your turn! Repeat the pattern</span>
              ) : (
                <span style={{ color: GAME_CONFIG.colors.light }}>Waiting for next color...</span>
              )}
            </div>

            {/* Simon Pads */}
            <div style={styles.padGrid}>
              {COLOR_CONFIG.map((color) => (
                <button
                  key={color.id}
                  onClick={() => handlePadClick(color.id)}
                  disabled={isPlayingSequence || gameState !== GAME_CONFIG.states.PLAYING}
                  style={{
                    ...styles.pad,
                    backgroundColor: color.hex,
                    boxShadow:
                      activePad === color.id
                        ? `0 0 30px rgba(${color.rgbGlow}, 0.8), inset 0 0 20px rgba(0, 0, 0, 0.4)`
                        : `0 0 10px rgba(${color.rgbGlow}, 0.4), inset 0 0 10px rgba(0, 0, 0, 0.3)`,
                    transform: activePad === color.id ? 'scale(0.95)' : 'scale(1)',
                  }}
                  title={`Press ${color.name} (or click)`}
                >
                  <span style={styles.padLabel}>{color.name}</span>
                </button>
              ))}
            </div>

            {/* Difficulty Badge */}
            <div style={styles.difficultyBadge}>
              Difficulty: <strong>{difficulty.toUpperCase()}</strong>
            </div>

            {/* Instructions */}
            <div style={styles.instructions}>
              Watch Simon play the sequence, then repeat it by clicking the colored pads.
              Each round adds a new color!
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.WIN && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.winTitle}>🏆 You Won! 🏆</h1>
              <div style={styles.maxLevel}>
                Maximum Level Reached: {levelText}
              </div>

              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Difficulty': difficulty.toUpperCase(),
                  'Levels Completed': levelText,
                }}
              />

              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🎮 Play Again
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.loseTitle}>💔 Game Over 💔</h1>
              <div style={styles.wrongLevel}>
                You reached Level {levelText}
              </div>

              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Difficulty': difficulty.toUpperCase(),
                  'Levels Completed': Math.max(0, levelText - 1),
                }}
              />

              <div style={styles.gameOverMessage}>
                You made a mistake in the sequence. Try again!
              </div>

              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🔄 Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    width: '100%',
    maxWidth: '600px',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    width: '100%',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1rem',
    marginBottom: '2rem',
    lineHeight: '1.6',
  },
  difficultyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '1.5rem',
  },
  difficultyButton: {
    padding: '1.5rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
    borderRadius: GAME_CONFIG.radius.md,
    transition: `all ${GAME_CONFIG.transitions.normal}`,
  },
  difficultyDesc: {
    fontSize: '0.75rem',
    display: 'block',
    marginTop: '0.5rem',
    opacity: 0.8,
  },
  levelDisplay: {
    backgroundColor: `rgba(102, 126, 234, 0.2)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1.5rem 3rem',
    textAlign: 'center',
  },
  levelLabel: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  levelValue: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '3rem',
    fontWeight: 'bold',
  },
  statusDisplay: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1rem',
    padding: '1rem',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `1px solid ${GAME_CONFIG.colors.accent2}`,
    borderRadius: GAME_CONFIG.radius.md,
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  padGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1.5rem',
    width: '100%',
    maxWidth: '350px',
    margin: '0 auto',
  },
  pad: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: `all ${GAME_CONFIG.transitions.fast}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#000',
    textShadow: '0 2px 5px rgba(0,0,0,0.5)',
  },
  padLabel: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  difficultyBadge: {
    backgroundColor: `rgba(102, 126, 234, 0.15)`,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '20px',
    padding: '0.5rem 1rem',
    color: GAME_CONFIG.colors.accent,
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  instructions: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  endScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  endCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
    maxWidth: '500px',
    textAlign: 'center',
  },
  winTitle: {
    color: GAME_CONFIG.colors.success,
    marginBottom: '1rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  loseTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  maxLevel: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '1.2rem',
    marginBottom: '1.5rem',
    fontWeight: 'bold',
  },
  wrongLevel: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.2rem',
    marginBottom: '1.5rem',
    fontWeight: 'bold',
  },
  gameOverMessage: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '1rem',
    marginTop: '1rem',
  },
  restartButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
};

export default SimonSays;
